import { CSSProperties, ReactNode, useState } from 'react';
import { PulseDot, TruckPhoto, USMap } from './components/shared';
import { SS_CONTACT } from './data/contact';
import { Lockup } from './components/Lockup';
import { QuotePanel } from './components/QuotePanel';

const A = {
  bg: '#F5F2EC',
  ink: '#1A1A1A',
  green: '#0F2A1D',
  red: '#B8351E',
  rule: 'rgba(26,26,26,0.14)',
  soft: 'rgba(15,42,29,0.06)',
};

const display = 'var(--font-display)';
const mono = 'var(--font-mono)';

function Eyebrow({ children, color = A.green }: { children: ReactNode; color?: string }) {
  return (
    <div
      style={{
        fontFamily: mono,
        fontSize: 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color,
        opacity: 0.85,
      }}
    >
      {children}
    </div>
  );
}

function Rule({ color = A.rule, mt = 12, mb = 12 }: { color?: string; mt?: number; mb?: number }) {
  return <hr style={{ border: 0, borderTop: `1px solid ${color}`, margin: `${mt}px 0 ${mb}px` }} />;
}

function Section({
  children,
  pad = '64px 56px',
  style,
}: {
  children: ReactNode;
  pad?: string;
  style?: CSSProperties;
}) {
  return (
    <section className="ss-section" style={{ padding: pad, ...style }}>
      {children}
    </section>
  );
}

export function App() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div
      style={{
        background: A.bg,
        color: A.ink,
        fontFamily: 'var(--font-sans)',
        fontSize: 14,
        lineHeight: 1.5,
        minHeight: '100%',
      }}
    >
      {/* ─── NAV ─── */}
      <header
        className="ss-nav"
        style={{
          padding: '20px 56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${A.rule}`,
          position: 'sticky',
          top: 0,
          background: A.bg,
          zIndex: 10,
        }}
      >
        <Lockup />
        <nav className="ss-nav-links" style={{ display: 'flex', gap: 28, fontSize: 13, color: A.ink }}>
          {['Services', 'Fleet', 'Coverage', 'Tracking', 'Brokers'].map((l) => (
            <a key={l} href={`#${l.toLowerCase()}`}>
              {l}
            </a>
          ))}
        </nav>
        <div className="ss-nav-right" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PulseDot color={A.green} />
          <a
            className="ss-nav-phone"
            href={`tel:${SS_CONTACT.phone.replace(/[^\d+]/g, '')}`}
            style={{ fontFamily: mono, fontSize: 12 }}
          >
            Dispatch · {SS_CONTACT.phone}
          </a>
          <button
            onClick={() => setQuoteOpen(true)}
            style={{
              background: A.green,
              color: A.bg,
              border: 0,
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              letterSpacing: '0.02em',
            }}
          >
            Get a quote →
          </button>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <Section pad="64px 56px 56px">
        <Eyebrow>Hotshot &amp; Dually Hauling · Est. 2026 · Fort Worth TX</Eyebrow>
        <h1
          className="ss-hero-headline"
          style={{
            fontFamily: display,
            fontSize: 132,
            lineHeight: 0.92,
            letterSpacing: '-0.03em',
            margin: '20px 0 32px',
            fontWeight: 500,
            color: A.green,
            textWrap: 'balance' as CSSProperties['textWrap'],
          }}
        >
          Freight that moves on the <em style={{ fontStyle: 'italic', color: A.red }}>first call.</em>
        </h1>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 1fr',
            gap: 56,
            alignItems: 'start',
          }}
          className="ss-coverage-grid"
        >
          <p
            style={{
              fontSize: 20,
              lineHeight: 1.45,
              color: 'rgba(26,26,26,0.78)',
              maxWidth: 640,
            }}
          >
            Hotshot, dually flatbed, and gooseneck capacity for brokers and dispatchers who need a
            carrier that picks up the phone, runs on time, and tracks every mile.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              onClick={() => setQuoteOpen(true)}
              style={{
                background: A.ink,
                color: A.bg,
                border: 0,
                padding: '14px 22px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Book a load →
            </button>
            <button
              style={{
                background: 'transparent',
                color: A.ink,
                border: `1px solid ${A.ink}`,
                padding: '14px 22px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Carrier packet
            </button>
          </div>
        </div>
      </Section>

      {/* ─── SERVICES ─── */}
      <Section pad="80px 56px" style={{ background: A.soft }}>
        <a id="services" />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginBottom: 40,
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Eyebrow>§ 02 — Capabilities</Eyebrow>
            <h2
              className="ss-section-heading"
              style={{
                fontFamily: display,
                fontSize: 56,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                margin: '14px 0 0',
                fontWeight: 500,
                color: A.ink,
              }}
            >
              Equipment for every <em style={{ fontStyle: 'italic' }}>shape</em> of load.
            </h2>
          </div>
          <a
            style={{
              fontFamily: mono,
              fontSize: 12,
              color: A.green,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            See full fleet ↗
          </a>
        </div>
        <div
          className="ss-services-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}
        >
          {(
            [
              { num: '01', name: 'Hotshot', spec: "1-ton dually + 40' gooseneck · up to 16,500 lb", tone: 'sky', rig: 'hotshot', notes: 'Expedited freight, partials, time-critical drops.' },
              { num: '02', name: 'Dually Flatbed', spec: "Bumper-pull · 20–30' deck · 10,000 lb", tone: 'depot', rig: 'dually-flatbed', notes: 'Construction materials, machinery, palletized.' },
              { num: '03', name: 'Step-Deck Gooseneck', spec: "40' · drops to 36\" · oversize / tall loads", tone: 'dusk', rig: 'stepdeck', notes: 'Equipment, agricultural, low-clearance freight.' },
            ] as const
          ).map((s) => (
            <ServiceCard key={s.num} {...s} />
          ))}
        </div>
      </Section>

      {/* ─── COVERAGE ─── */}
      <Section pad="80px 56px">
        <a id="coverage" />
        <div
          className="ss-coverage-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.3fr',
            gap: 56,
            alignItems: 'center',
          }}
        >
          <div>
            <Eyebrow>§ 03 — Lanes we run</Eyebrow>
            <h2
              className="ss-section-heading"
              style={{
                fontFamily: display,
                fontSize: 56,
                lineHeight: 1,
                margin: '14px 0 20px',
                fontWeight: 500,
                letterSpacing: '-0.02em',
              }}
            >
              Anchored in Fort Worth.
              <br />
              <em style={{ fontStyle: 'italic', color: A.red }}>Running the whole 48.</em>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(26,26,26,0.78)', maxWidth: 460 }}>
              We&rsquo;re built around the Texas Triangle but consistently run TX → Southeast and TX → Midwest.
              Reload lanes back to DFW most weeks.
            </p>
            <Rule mt={24} mb={20} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 13 }}>
              {(
                [
                  ['Primary hub', 'Fort Worth, TX'],
                  ['Density lanes', 'I-20, I-30, I-35, I-45'],
                  ['Avg. transit', '1.2 days / 600 mi'],
                  ['Deadhead policy', '< 12% target'],
                ] as const
              ).map(([k, v]) => (
                <div key={k}>
                  <div
                    style={{
                      fontFamily: mono,
                      fontSize: 10,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'rgba(26,26,26,0.5)',
                    }}
                  >
                    {k}
                  </div>
                  <div style={{ marginTop: 2, fontWeight: 500 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: A.bg, border: `1px solid ${A.rule}`, padding: 24 }}>
            <USMap
              stroke={A.ink}
              dotColor={A.red}
              laneColor={A.green}
              dots={[
                'Fort Worth',
                'Houston',
                'Atlanta',
                'Memphis',
                'Chicago',
                'Phoenix',
                'Denver',
                'Nashville',
                'Los Angeles',
                'Charlotte',
                'Oklahoma City',
                'St. Louis',
              ]}
              lanes={[
                ['Fort Worth', 'Atlanta'],
                ['Fort Worth', 'Chicago'],
                ['Fort Worth', 'Phoenix'],
                ['Fort Worth', 'Denver'],
                ['Fort Worth', 'Charlotte'],
                ['Fort Worth', 'Los Angeles'],
              ]}
            />
            <div
              style={{
                display: 'flex',
                gap: 24,
                marginTop: 16,
                fontFamily: mono,
                fontSize: 11,
                color: 'rgba(26,26,26,0.6)',
                flexWrap: 'wrap',
              }}
            >
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: A.red,
                    marginRight: 6,
                  }}
                />
                Active service city
              </span>
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 1.5,
                    borderTop: `2px dashed ${A.green}`,
                    marginRight: 6,
                    verticalAlign: 'middle',
                  }}
                />
                Common lane
              </span>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── PROCESS ─── */}
      <Section pad="80px 56px" style={{ borderTop: `1px solid ${A.rule}` }}>
        <Eyebrow>§ 04 — How a load runs with us</Eyebrow>
        <h2
          className="ss-section-heading"
          style={{
            fontFamily: display,
            fontSize: 56,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            margin: '14px 0 40px',
            fontWeight: 500,
            color: A.ink,
          }}
        >
          One dispatcher. <em style={{ fontStyle: 'italic' }}>Four touchpoints.</em>
        </h2>
        <div
          className="ss-process-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 32,
            position: 'relative',
          }}
        >
          <div
            className="ss-process-rule"
            style={{ position: 'absolute', left: 0, right: 0, top: 18, height: 1, background: A.rule }}
          />
          {(
            [
              ['01', 'Quote', 'Send load details. Average reply in under 90 seconds with a firm rate.', '06:14'],
              ['02', 'Dispatch', 'A named operator owns the load. Direct line, no extension transfers.', '06:42'],
              ['03', 'Track', 'Live GPS link the moment we pick up — shareable with your shipper.', '08:44'],
              ['04', 'Deliver', 'PoD photo + signature emailed within 10 minutes of unloading.', '14:20'],
            ] as const
          ).map(([num, name, body, time]) => (
            <div key={num} style={{ position: 'relative', paddingTop: 28 }}>
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  background: A.green,
                  border: `3px solid ${A.bg}`,
                  boxSizing: 'content-box',
                }}
              />
              <div
                style={{
                  fontFamily: mono,
                  fontSize: 11,
                  color: A.red,
                  letterSpacing: '0.14em',
                }}
              >
                STEP / {num} · {time}
              </div>
              <h3
                style={{
                  fontFamily: display,
                  fontSize: 28,
                  margin: '8px 0 8px',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                }}
              >
                {name}
              </h3>
              <p style={{ fontSize: 13.5, color: 'rgba(26,26,26,0.72)', lineHeight: 1.55 }}>{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── LIVE TRACKING TEASER ─── */}
      <Section pad="80px 56px" style={{ background: A.green, color: A.bg }}>
        <a id="tracking" />
        <div
          className="ss-tracking-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 56,
            alignItems: 'center',
          }}
        >
          <div>
            <Eyebrow color="rgba(245,242,236,0.6)">§ 06 — Broker portal</Eyebrow>
            <h2
              className="ss-section-heading"
              style={{
                fontFamily: display,
                fontSize: 56,
                lineHeight: 1,
                margin: '14px 0 20px',
                fontWeight: 500,
                letterSpacing: '-0.02em',
              }}
            >
              Live tracking.
              <br />
              One link. Every load.
            </h2>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.55,
                color: 'rgba(245,242,236,0.78)',
                maxWidth: 480,
              }}
            >
              Every load gets a shareable tracking page. ETAs update from the driver&rsquo;s phone — your shipper sees
              the same screen you do.
            </p>
            <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                style={{
                  background: A.bg,
                  color: A.green,
                  border: 0,
                  padding: '13px 20px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                See sample load →
              </button>
              <button
                style={{
                  background: 'transparent',
                  color: A.bg,
                  border: `1px solid ${A.bg}`,
                  padding: '13px 20px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Broker login
              </button>
            </div>
          </div>
          <div
            style={{
              background: '#0a1c14',
              border: '1px solid rgba(245,242,236,0.12)',
              padding: 24,
              fontFamily: mono,
              fontSize: 12,
              color: A.bg,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <PulseDot color="#9bd9a4" />
                <span>LOAD SS-24018 · IN TRANSIT</span>
              </div>
              <span style={{ color: 'rgba(245,242,236,0.55)' }}>updated 23s ago</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 18,
                marginBottom: 22,
              }}
            >
              <KV mono="ORIGIN" v="Fort Worth, TX" />
              <KV mono="DEST" v="Atlanta, GA" />
              <KV mono="ETA" v="Thu 14:20 CT" />
              <KV mono="PROGRESS" v="612 / 928 mi · 66%" />
            </div>
            <div style={{ position: 'relative', height: 4, background: 'rgba(245,242,236,0.15)' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '66%',
                  background: '#9bd9a4',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '66%',
                  top: -3,
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  background: '#9bd9a4',
                  transform: 'translateX(-5px)',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 8,
                color: 'rgba(245,242,236,0.55)',
              }}
            >
              <span>FTW · Mon 06:14</span>
              <span>ATL · Thu ~14:20</span>
            </div>
            <Rule color="rgba(245,242,236,0.12)" mt={20} mb={16} />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                color: 'rgba(245,242,236,0.78)',
              }}
            >
              <LogRow t="14:23" m="Crossed MS / AL border · I-20 E" />
              <LogRow t="12:01" m="Fuel + DOT break · Meridian, MS" />
              <LogRow t="08:44" m="Departed Shreveport, LA" />
            </div>
          </div>
        </div>
      </Section>

      {/* ─── FOOTER ─── */}
      <footer style={{ padding: '48px 56px 32px', borderTop: `1px solid ${A.rule}` }}>
        <div
          className="ss-footer-grid"
          style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 40 }}
        >
          <div>
            <Lockup />
            <p style={{ marginTop: 14, fontSize: 13, color: 'rgba(26,26,26,0.65)', maxWidth: 320 }}>
              ShipStack Inc. Hotshot &amp; dually freight. Fort Worth-based, 48-state coverage.
            </p>
          </div>
          <FootCol h="Dispatch" rows={[SS_CONTACT.phone, SS_CONTACT.email]} />
          <FootCol h="Address" rows={[SS_CONTACT.addrLine1, SS_CONTACT.addrLine2]} />
        </div>
        <Rule mt={36} mb={14} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: mono,
            fontSize: 11,
            color: 'rgba(26,26,26,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span>© {new Date().getFullYear()} ShipStack Inc.</span>
          <span>Fort Worth · Texas</span>
        </div>
      </footer>

      <QuotePanel open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}

function ServiceCard({
  num,
  name,
  spec,
  tone,
  rig,
  notes,
}: {
  num: string;
  name: string;
  spec: string;
  tone: 'sky' | 'depot' | 'dusk';
  rig: 'hotshot' | 'dually-flatbed' | 'stepdeck';
  notes: string;
}) {
  return (
    <article
      style={{
        background: A.bg,
        border: `1px solid ${A.rule}`,
        transition: 'transform 180ms ease-out, border-color 180ms ease-out',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = A.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = A.rule;
      }}
    >
      <TruckPhoto
        subject={`${name} rig`}
        framing="hero plate · low angle"
        tone={tone}
        rig={rig}
        ratio="4 / 3"
        label={false}
      />
      <div style={{ padding: '20px 22px 24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: mono,
            fontSize: 11,
            color: 'rgba(26,26,26,0.55)',
          }}
        >
          <span>SERVICE / {num}</span>
          <span>AVAILABLE</span>
        </div>
        <h3
          style={{
            fontFamily: display,
            fontSize: 30,
            margin: '8px 0 4px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          {name}
        </h3>
        <div style={{ fontSize: 13, color: A.green, fontFamily: mono }}>{spec}</div>
        <p style={{ fontSize: 13, color: 'rgba(26,26,26,0.7)', marginTop: 12, lineHeight: 1.5 }}>{notes}</p>
      </div>
    </article>
  );
}

function KV({ mono: label, v }: { mono: string; v: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.12em',
          color: 'rgba(245,242,236,0.55)',
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 14 }}>{v}</div>
    </div>
  );
}

function LogRow({ t, m }: { t: string; m: string }) {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 11.5 }}>
      <span style={{ color: 'rgba(245,242,236,0.5)', width: 48 }}>{t}</span>
      <span>{m}</span>
    </div>
  );
}

function FootCol({ h, rows }: { h: string; rows: string[] }) {
  return (
    <div>
      <div
        style={{
          fontFamily: mono,
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(26,26,26,0.5)',
          marginBottom: 10,
        }}
      >
        {h}
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
          {r}
        </div>
      ))}
    </div>
  );
}
